export type FontPairing = {
  name: string
  display: string
  body: string
  description: string
}

export type PairingCategory = {
  id: string
  name: string
  description: string
  pairings: FontPairing[]
}

export const PAIRING_CATEGORIES: PairingCategory[] = [
  {
    id: 'spooky',
    name: 'Spooky',
    description: 'Haunted posters and eerie storytelling vibes.',
    pairings: [
      {
        name: 'Haunted Carnival',
        display: 'Creepster',
        body: 'IM Fell English',
        description: 'Classic horror headline with vintage body texture.',
      },
      {
        name: 'Ghost Story',
        display: 'Nosifer',
        body: 'Crimson Pro',
        description: 'Sharp, cinematic titles paired with readable serif.',
      },
    ],
  },
  {
    id: 'editorial',
    name: 'Editorial',
    description: 'Magazine and long-form reading with elegance.',
    pairings: [
      {
        name: 'Broadsheet',
        display: 'Playfair Display',
        body: 'Source Serif 4',
        description: 'Classic editorial hierarchy with refined body text.',
      },
      {
        name: 'Modern Press',
        display: 'DM Serif Display',
        body: 'Source Sans 3',
        description: 'Serif headlines with clean sans for clarity.',
      },
    ],
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Quiet, neutral, and design-led.',
    pairings: [
      {
        name: 'Swiss Calm',
        display: 'Space Grotesk',
        body: 'Inter',
        description: 'Geometric headline with utilitarian body.',
      },
      {
        name: 'Soft Modern',
        display: 'Sora',
        body: 'Work Sans',
        description: 'Rounded modern display with understated body.',
      },
    ],
  },
  {
    id: 'luxury',
    name: 'Luxury',
    description: 'High-end branding and premium product feel.',
    pairings: [
      {
        name: 'Maison',
        display: 'Bodoni Moda',
        body: 'Cormorant Garamond',
        description: 'High-contrast glamour with refined text.',
      },
      {
        name: 'Gallery',
        display: 'Cinzel',
        body: 'Libre Baskerville',
        description: 'Classic engraving style paired with bookish serif.',
      },
    ],
  },
  {
    id: 'tech',
    name: 'Tech',
    description: 'SaaS, dev tools, and modern interfaces.',
    pairings: [
      {
        name: 'Control Panel',
        display: 'Space Grotesk',
        body: 'IBM Plex Sans',
        description: 'Futuristic headline with clean system body.',
      },
      {
        name: 'Console',
        display: 'Rajdhani',
        body: 'Inter',
        description: 'Technical display with readable UI body.',
      },
    ],
  },
  {
    id: 'startup',
    name: 'Startup',
    description: 'Friendly, confident, and conversion-ready.',
    pairings: [
      {
        name: 'Launchpad',
        display: 'Poppins',
        body: 'DM Sans',
        description: 'Bright, energetic display with clean body copy.',
      },
      {
        name: 'Pitch Deck',
        display: 'Montserrat',
        body: 'Roboto',
        description: 'Bold presentation feel with familiar body.',
      },
    ],
  },
  {
    id: 'playful',
    name: 'Playful',
    description: 'Fun, approachable, and youthful.',
    pairings: [
      {
        name: 'Bubble Pop',
        display: 'Baloo 2',
        body: 'Nunito',
        description: 'Bouncy headline with friendly rounded body.',
      },
      {
        name: 'Sparkle',
        display: 'Fredoka',
        body: 'Quicksand',
        description: 'Whimsical display with soft body.',
      },
    ],
  },
  {
    id: 'retro',
    name: 'Retro',
    description: 'Vintage, analog, and nostalgic.',
    pairings: [
      {
        name: 'Vinyl',
        display: 'Bebas Neue',
        body: 'Libre Franklin',
        description: 'Bold condensed display with classic sans.',
      },
      {
        name: 'Old School',
        display: 'Abril Fatface',
        body: 'Lora',
        description: 'Retro serif title with readable serif body.',
      },
    ],
  },
  {
    id: 'nature',
    name: 'Nature',
    description: 'Organic, calm, and earthy.',
    pairings: [
      {
        name: 'Forest Trail',
        display: 'Amatic SC',
        body: 'Merriweather',
        description: 'Handmade headline with grounded body.',
      },
      {
        name: 'Meadow',
        display: 'Pacifico',
        body: 'Open Sans',
        description: 'Script display with clean sans body.',
      },
    ],
  },
  {
    id: 'wellness',
    name: 'Wellness',
    description: 'Soft, calming, and human.',
    pairings: [
      {
        name: 'Retreat',
        display: 'Cormorant',
        body: 'Nunito Sans',
        description: 'Elegant serif with gentle sans body.',
      },
      {
        name: 'Mindful',
        display: 'Satisfy',
        body: 'Source Sans 3',
        description: 'Personal script headline with clean body.',
      },
    ],
  },
  {
    id: 'finance',
    name: 'Finance',
    description: 'Trust, clarity, and professionalism.',
    pairings: [
      {
        name: 'Ledger',
        display: 'Libre Baskerville',
        body: 'Roboto',
        description: 'Traditional headline with familiar sans body.',
      },
      {
        name: 'Institution',
        display: 'Merriweather',
        body: 'Lato',
        description: 'Authoritative serif with warm sans.',
      },
    ],
  },
  {
    id: 'ecommerce',
    name: 'Ecommerce',
    description: 'Product-forward and fast to scan.',
    pairings: [
      {
        name: 'Catalog',
        display: 'Oswald',
        body: 'Source Sans 3',
        description: 'Condensed display with crisp UI body.',
      },
      {
        name: 'Storefront',
        display: 'Montserrat',
        body: 'Open Sans',
        description: 'Clean display with universal body.',
      },
    ],
  },
  {
    id: 'fashion',
    name: 'Fashion',
    description: 'Editorial glamour with modern edge.',
    pairings: [
      {
        name: 'Runway',
        display: 'Didact Gothic',
        body: 'Playfair Display',
        description: 'Minimal display with luxe serif body.',
      },
      {
        name: 'Atelier',
        display: 'Bodoni Moda',
        body: 'Manrope',
        description: 'High fashion headline with clean body.',
      },
    ],
  },
  {
    id: 'travel',
    name: 'Travel',
    description: 'Wanderlust and photo-forward design.',
    pairings: [
      {
        name: 'Postcard',
        display: 'Lobster',
        body: 'Lora',
        description: 'Script headline with classic body.',
      },
      {
        name: 'Explorer',
        display: 'Montserrat',
        body: 'Muli',
        description: 'Adventure-ready sans with clean body.',
      },
    ],
  },
  {
    id: 'food',
    name: 'Food',
    description: 'Warm, appetizing, and handcrafted.',
    pairings: [
      {
        name: 'Bistro',
        display: 'Playball',
        body: 'Source Sans 3',
        description: 'Handwritten display with readable body.',
      },
      {
        name: 'Bakery',
        display: 'Cookie',
        body: 'Nunito',
        description: 'Soft script with friendly body.',
      },
    ],
  },
  {
    id: 'music',
    name: 'Music',
    description: 'Rhythmic, energetic, and bold.',
    pairings: [
      {
        name: 'Festival',
        display: 'Bebas Neue',
        body: 'Barlow',
        description: 'Poster headline with clean body.',
      },
      {
        name: 'Synthwave',
        display: 'Monoton',
        body: 'Space Grotesk',
        description: 'Retro-futuristic display with modern body.',
      },
    ],
  },
  {
    id: 'sports',
    name: 'Sports',
    description: 'Bold, dynamic, and high impact.',
    pairings: [
      {
        name: 'Arena',
        display: 'Anton',
        body: 'Roboto Condensed',
        description: 'Huge display with compact body.',
      },
      {
        name: 'Scoreboard',
        display: 'Bungee',
        body: 'Inter',
        description: 'Graphic display with neutral body.',
      },
    ],
  },
  {
    id: 'education',
    name: 'Education',
    description: 'Credible, approachable, and clear.',
    pairings: [
      {
        name: 'Campus',
        display: 'Merriweather',
        body: 'Source Sans 3',
        description: 'Academic headline with clean body.',
      },
      {
        name: 'Lesson Plan',
        display: 'Libre Baskerville',
        body: 'PT Sans',
        description: 'Bookish title with straightforward body.',
      },
    ],
  },
  {
    id: 'kids',
    name: 'Kids',
    description: 'Playful, readable, and bright.',
    pairings: [
      {
        name: 'Story Time',
        display: 'Chewy',
        body: 'Nunito Sans',
        description: 'Friendly bubble headline with clear body.',
      },
      {
        name: 'Playground',
        display: 'Luckiest Guy',
        body: 'Quicksand',
        description: 'Cartoon headline with soft body.',
      },
    ],
  },
  {
    id: 'futuristic',
    name: 'Futuristic',
    description: 'Sci-fi, bold shapes, and sleek minimalism.',
    pairings: [
      {
        name: 'Orbit',
        display: 'Orbitron',
        body: 'Rajdhani',
        description: 'Sci-fi display with tech body.',
      },
      {
        name: 'Neon Grid',
        display: 'Audiowide',
        body: 'Space Grotesk',
        description: 'Retro-future headline with clean body.',
      },
    ],
  },
]
